/**
 * The most paints a field keeps its last drawing for while a new map's picture has nothing composed: about half a second
 * of moves, so a picture that never composes cannot freeze the field.
 */
export const BARE_HOLD_PAINTS = 30;

/**
 * What a field remembers of its last paint: the picture source it drew from, whether that drawing had any ground
 * composed, and how many paints it has held it for.
 */
export interface FieldHold {
  source: unknown;
  drawn: boolean;
  held: number;
}

/**
 * Makes the hold of a field that has not painted.
 *
 * @returns The hold.
 */
export function newFieldHold(): FieldHold {
  return { source: null, drawn: false, held: 0 };
}

/**
 * Whether a field keeps its last drawing instead of painting (ADR-147 decision 19): the map's picture source has changed,
 * as at a crossing's change of stage (the old stage takes its pictures away, then the new one gives its own), the source
 * now has no picture or one with nothing composed yet (version 1 or less), and the last drawing had ground on it, for at
 * most `BARE_HOLD_PAINTS` paints. So the minimap never shows a bare slate for the
 * frame between the stage changing and the new map's first chunk being composed.
 *
 * @param hold The field's hold, updated in place.
 * @param source The picture source now, or null.
 * @param version The version of the source's picture, -1 when it has none.
 * @param crossing Whether the change of source is a crossing's (or the gap before one), the only change it holds for: a
 *   door's change of map happens behind its fade and paints the new pictures at once.
 * @returns True when the field should not paint now.
 */
export function keepsLastField(hold: FieldHold, source: unknown, version: number, crossing: boolean): boolean {
  if (crossing && source !== hold.source && hold.drawn && version <= 1 && hold.held < BARE_HOLD_PAINTS) {
    hold.held += 1;
    return true;
  }
  hold.source = source;
  hold.drawn = version > 1;
  hold.held = 0;
  return false;
}

/**
 * A picture source as a field's hold reads it: the chunks a slot still owes, and whether a crossing's change of stage gave
 * it (`MapPictureSource`).
 */
export interface HeldSource {
  readonly owed?: (slot: string) => number;
  readonly crossing?: boolean;
}

/**
 * The decision a field's paint asks (`keepsLastField`), from what it holds: a picture that still owes chunks counts as
 * bare even after its first chunk is composed, so a crossing never shows slate past the chunk composed; the hold applies
 * to a source a crossing gave and to the gap with no source before it.
 *
 * @param hold The field's hold, updated in place.
 * @param source The picture source now, or null.
 * @param picture The source's picture for the field, or null.
 * @param slot The field's slot.
 * @returns True when the field should not paint now.
 */
export function keepsFieldFor(hold: FieldHold, source: HeldSource | null, picture: { readonly version: number } | null, slot = "hud"): boolean {
  const bare = picture !== null && (source?.owed?.(slot) ?? 0) > 0;
  const version = picture === null ? -1 : bare ? Math.min(1, picture.version) : picture.version;
  return keepsLastField(hold, source, version, source === null || source.crossing === true);
}
